import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { login as apiLogin, getMe, getMyPermissions } from '../api';
import { message } from 'antd';

export interface User {
  id: number;
  email: string;
  displayName: string;
  role: string;        // Keep for compat
  roles: string[];     // New multi-role
  departmentId: number;
  departmentCode: string;
  /** Extra departments assigned in Admin (multi-department users). */
  extraDepartmentIds?: number[];
}

const ACCESS_HIERARCHY: Record<string, number> = { none: 0, read_only: 1, create: 2, full: 3 };

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  isApprover: boolean;
  isProcurement: boolean;
  isRequester: boolean;
  isAccountManager: boolean;
  hasRole: (role: string) => boolean;
  permissions: Record<string, string>;
  canAccess: (resource: string, minLevel?: 'read_only' | 'create' | 'full') => boolean;
  accessLevel: (resource: string) => string;
  permissionsLoading: boolean;
  /** Home department plus extra departments assigned in Admin. */
  myDepartmentIds: number[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState<boolean>(true);
  const [permissions, setPermissions] = useState<Record<string, string>>(() => {
    const stored = localStorage.getItem('permissions');
    return stored ? JSON.parse(stored) : {};
  });
  const [permissionsLoading, setPermissionsLoading] = useState<boolean>(false);

  // Load permissions from API
  const loadPermissions = useCallback(async () => {
    try {
      setPermissionsLoading(true);
      const perms = await getMyPermissions();
      setPermissions(perms);
      localStorage.setItem('permissions', JSON.stringify(perms));
    } catch {
      // If permissions endpoint fails (e.g., new deployment), fallback to empty
      console.warn('Failed to load permissions, using defaults');
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      if (token) {
        try {
          const userData = await getMe();
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          // Load permissions after auth is verified
          await loadPermissions();
        } catch {
          setToken(null);
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('permissions');
        }
      }
      setLoading(false);
    };
    verifyAuth();
  }, [token, loadPermissions]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await apiLogin(email, password);
    const { token: newToken, user: newUser } = response;
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    // Permissions will load via the token useEffect
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setPermissions({});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    message.success('Logged out successfully');
  }, []);

  const hasRole = (r: string) => {
    const userRoles = user?.roles?.length ? user.roles : user?.role ? [user.role] : [];
    return userRoles.includes(r);
  };
  const isAdmin = hasRole('admin');
  const isApprover = hasRole('approver') || isAdmin;
  const isProcurement = hasRole('procurement_officer') || isAdmin;
  const isRequester = hasRole('requester') && !isAdmin && !isApprover && !isProcurement && !hasRole('account_manager');
  const isAccountManager = hasRole('account_manager') || isAdmin;

  // Resolve access level for a resource (page→module downward, module→pages upward)
  const accessLevel = useCallback((resource: string): string => {
    if (isAdmin) return 'full';
    // Check exact resource permission
    if (permissions[resource] && permissions[resource] !== 'none') {
      return permissions[resource];
    }
    // Downward fallback: page-level falls back to module-level
    const dotIndex = resource.indexOf('.');
    if (dotIndex > 0) {
      const mod = resource.substring(0, dotIndex);
      if (permissions[mod] && permissions[mod] !== 'none') return permissions[mod];
    }
    // Upward fallback: module-level checks if ANY sub-page has access
    if (dotIndex < 0) {
      const prefix = resource + '.';
      let highest = 0;
      for (const key of Object.keys(permissions)) {
        if (key.startsWith(prefix) && permissions[key] !== 'none') {
          highest = Math.max(highest, ACCESS_HIERARCHY[permissions[key]] || 0);
        }
      }
      if (highest > 0) {
        const found = Object.entries(ACCESS_HIERARCHY).find(([, v]) => v === highest);
        return found ? found[0] : 'none';
      }
    }
    // If permissions haven't loaded yet but user has legacy role-based access, grant access
    // Only for purchasing and crm modules — admin module always requires explicit permission
    if (Object.keys(permissions).length === 0 && !permissionsLoading && !resource.startsWith('admin')) {
      const legacyFullRoles = ['admin', 'approver', 'procurement_officer', 'account_manager'];
      const userRoles = user?.roles?.length ? user.roles : user?.role ? [user.role] : [];
      return userRoles.some(r => legacyFullRoles.includes(r)) ? 'full' : 'read_only';
    }
    return permissions[resource] || 'none';
  }, [isAdmin, permissions, permissionsLoading, user]);

  // Check if user can access a resource at minimum the given level
  // Is this resource registered on the server? /api/permissions/my returns every
  // registered resource for admins, so a key missing from the map means the backend
  // this client is talking to does not have that module/page at all (e.g. a client
  // bundle that carries pages the production server never received). Page-level keys
  // must match exactly; a module key (no dot) is registered if it or any of its pages is.
  const isRegistered = useCallback((resource: string): boolean => {
    const keys = Object.keys(permissions);
    if (keys.length === 0) return true; // not loaded / endpoint failed — keep legacy behaviour
    if (permissions[resource] !== undefined) return true;
    if (resource.indexOf('.') < 0) return keys.some((k) => k.startsWith(resource + '.'));
    return false;
  }, [permissions]);

  const myDepartmentIds = React.useMemo(() => {
    if (!user) return [];
    return Array.from(new Set([user.departmentId, ...(user.extraDepartmentIds || [])].filter((n) => Number.isFinite(n) && n > 0)));
  }, [user]);

  const canAccess = useCallback((resource: string, minLevel: 'read_only' | 'create' | 'full' = 'read_only'): boolean => {
    if (isAdmin) return isRegistered(resource);
    const level = accessLevel(resource);
    return (ACCESS_HIERARCHY[level] || 0) >= (ACCESS_HIERARCHY[minLevel] || 0);
  }, [isAdmin, isRegistered, accessLevel]);

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, logout, isAdmin, isApprover, isProcurement, isRequester, isAccountManager, hasRole, permissions, canAccess, accessLevel, permissionsLoading, myDepartmentIds }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const ProtectedRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
